import React, { useState } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Car } from 'lucide-react';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [registerStep, setRegisterStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    vat_number: '',
    billing_street: '',
    billing_postal_code: '',
    billing_city: '',
    billing_country: 'Belgique',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, user } = useAuth();

  const verified = searchParams.get('verified') === '1';

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = isLogin
        ? await login(formData.email, formData.password)
        : await register({
            email: formData.email,
            password: formData.password,
            name: formData.name,
            company: formData.company,
            vat_number: formData.vat_number || null,
            billing_street: formData.billing_street,
            billing_postal_code: formData.billing_postal_code,
            billing_city: formData.billing_city,
            billing_country: formData.billing_country,
          });

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) {
      setError('Le nom est requis');
      return;
    }
    if (!formData.email.trim()) {
      setError('L\'email est requis');
      return;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setRegisterStep(2);
  };

  const handleBackStep = () => {
    setError('');
    setRegisterStep(1);
  };

  const inputClasses = "mt-1 block w-full px-3 py-2.5 bg-dark-input border border-dark-border rounded-lg text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-carmin focus:border-carmin";

  const containerMaxWidth = isLogin ? 'sm:max-w-md' : 'sm:max-w-lg';

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Car className="h-12 w-12 text-carmin" />
        </div>
        <h2 className="mt-4 text-center text-3xl font-extrabold text-primary">
          Carmin
        </h2>
        <h3 className="mt-2 text-center text-xl text-secondary">
          Publication automatique de véhicules
        </h3>
      </div>

      <div className={`mt-8 sm:mx-auto sm:w-full ${containerMaxWidth}`}>
        <div className="bg-dark-card py-8 px-4 shadow-xl border border-dark-border sm:rounded-xl sm:px-10">

          {!isLogin && (
            <p className="text-center text-sm text-secondary mb-6">
              {`Étape ${registerStep}/2`}
            </p>
          )}

          {isLogin && verified && (
            <div className="mb-6 text-sm text-green-400 text-center">
              Email vérifié avec succès !
            </div>
          )}

          {/* -------- LOGIN FORM -------- */}
          {isLogin && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-label">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-label">
                  Mot de passe
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              {error && (
                <div className="text-sm text-red-400">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-semibold text-white bg-carmin hover:bg-carmin-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-carmin disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Chargement...' : 'Se connecter'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(false);
                    setRegisterStep(1);
                    setError('');
                  }}
                  className="text-sm text-carmin hover:text-carmin-light"
                >
                  Pas encore de compte ? S'inscrire
                </button>
              </div>
            </form>
          )}

          {/* -------- REGISTER STEP 1 — Compte -------- */}
          {!isLogin && registerStep === 1 && (
            <form className="space-y-6" onSubmit={handleNextStep}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-label">
                  Nom complet
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-label">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-label">
                  Mot de passe
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              {error && (
                <div className="text-sm text-red-400">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-semibold text-white bg-carmin hover:bg-carmin-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-carmin transition-colors"
                >
                  Suivant
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(true);
                    setRegisterStep(1);
                    setError('');
                  }}
                  className="text-sm text-carmin hover:text-carmin-light"
                >
                  Déjà un compte ? Se connecter
                </button>
              </div>
            </form>
          )}

          {/* -------- REGISTER STEP 2 — Entreprise & Facturation -------- */}
          {!isLogin && registerStep === 2 && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-label">
                  Nom de l'entreprise
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              <div>
                <label htmlFor="vat_number" className="block text-sm font-medium text-label">
                  Numéro de TVA (optionnel)
                </label>
                <input
                  id="vat_number"
                  name="vat_number"
                  type="text"
                  placeholder="BE0123456789"
                  value={formData.vat_number}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              <div>
                <label htmlFor="billing_street" className="block text-sm font-medium text-label">
                  Adresse
                </label>
                <input
                  id="billing_street"
                  name="billing_street"
                  type="text"
                  required
                  value={formData.billing_street}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="billing_postal_code" className="block text-sm font-medium text-label">
                    Code postal
                  </label>
                  <input
                    id="billing_postal_code"
                    name="billing_postal_code"
                    type="text"
                    required
                    value={formData.billing_postal_code}
                    onChange={handleChange}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="billing_city" className="block text-sm font-medium text-label">
                    Ville
                  </label>
                  <input
                    id="billing_city"
                    name="billing_city"
                    type="text"
                    required
                    value={formData.billing_city}
                    onChange={handleChange}
                    className={inputClasses}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="billing_country" className="block text-sm font-medium text-label">
                  Pays
                </label>
                <input
                  id="billing_country"
                  name="billing_country"
                  type="text"
                  required
                  value={formData.billing_country}
                  onChange={handleChange}
                  className={inputClasses}
                />
              </div>

              {error && (
                <div className="text-sm text-red-400">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBackStep}
                  className="flex-1 flex justify-center py-3 px-4 border border-dark-border rounded-lg text-sm font-semibold text-secondary bg-dark-input hover:bg-dark-border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-carmin transition-colors"
                >
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-semibold text-white bg-carmin hover:bg-carmin-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-carmin disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Chargement...' : 'Commencer mon essai gratuit'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(true);
                    setRegisterStep(1);
                    setError('');
                  }}
                  className="text-sm text-carmin hover:text-carmin-light"
                >
                  Déjà un compte ? Se connecter
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login;
